import { useState, useEffect } from "react";
import MarketCountdown from "@/components/MarketCountdown";
import { Link } from "react-router-dom";
import { getMajorStocks, getMarketOverview, getTopCrypto, getPortfolio, getWatchlist, getMarketStatus, getWoodrowAccount } from "@/lib/api";
import { PortfolioSummary } from "@/components/PortfolioSummary";
import AllStocksPanel from "@/components/AllStocksPanel";
import { useAuth } from "@/lib/AuthContext";
import MarketTicker from "@/components/MarketTicker";
import { TrendingUp, TrendingDown, ArrowRight, Star, Trash2, BarChart3, Radio, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { removeFromWatchlist } from "@/lib/api";
import { toast } from "sonner";

function StockRow({ stock }) {
  const hasData = stock.price != null;
  const isPositive = (stock.change_pct || 0) >= 0;
  return (
    <Link
      to={`/stock/${stock.symbol}`}
      className="flex items-center justify-between px-4 py-3 panel-hover border-b border-bt-border-subtle last:border-0"
      data-testid={`stock-row-${stock.symbol}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 bg-bt-surface-2 border border-bt-border-subtle flex items-center justify-center shrink-0">
          <span className="font-mono text-xs font-bold text-bt-gold">{stock.symbol.slice(0, 2)}</span>
        </div>
        <div className="min-w-0">
          <p className="font-mono text-sm font-medium text-bt-text-primary truncate">{stock.symbol}</p>
          <p className="text-xs text-bt-text-tertiary truncate">{stock.name}</p>
        </div>
      </div>
      <div className="text-right shrink-0 ml-3">
        {hasData ? (
          <>
            <p className="font-mono text-sm font-medium text-bt-text-primary">${stock.price.toLocaleString()}</p>
            <p className={`font-mono text-xs ${isPositive ? "text-bt-positive" : "text-bt-negative"}`}>
              {isPositive ? "+" : ""}{stock.change_pct}%
            </p>
          </>
        ) : (
          <p className="font-mono text-xs text-bt-text-tertiary">Awaiting data</p>
        )}
      </div>
    </Link>
  );
}

function SectorBadge({ sector, count }) {
  return (
    <div className="bg-bt-surface-2 border border-bt-border-subtle px-3 py-2 flex items-center justify-between">
      <span className="text-xs font-body text-bt-text-secondary">{sector}</span>
      <span className="font-mono text-xs text-bt-gold">{count}</span>
    </div>
  );
}

export default function Dashboard() {
  const [stocks, setStocks] = useState([]);
  const [overview, setOverview] = useState(null);
  const [crypto, setCrypto] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [woodrow, setWoodrow] = useState(null);
  const { user } = useAuth();
  const [watchlist, setWatchlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [marketStatus, setMarketStatus] = useState(null);

  const fetchAll = () => {
    Promise.all([
      getMajorStocks().catch(() => ({ data: [] })),
      getMarketOverview().catch(() => ({ data: null })),
      getTopCrypto().catch(() => ({ data: [] })),
      getPortfolio().catch(() => ({ data: [] })),
      getWatchlist().catch(() => ({ data: [] })),
      getMarketStatus().catch(() => ({ data: null })),
      getWoodrowAccount().catch(() => ({ data: null })),
    ]).then(([s, o, c, p, w, ms, wa]) => {
      setWoodrow(wa.data || null);
      setStocks(s.data || []);
      setOverview(o.data || null);
      setCrypto((c.data || []).slice(0, 5));
      setPortfolio(p.data || []);
      setWatchlist(w.data || []);
      setMarketStatus(ms.data || null);
      setLoading(false);
      setLastUpdated(new Date());
    });
  };

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleRemoveWatchlist = async (symbol) => {
    try {
      await removeFromWatchlist(symbol);
      setWatchlist(prev => prev.filter(w => w.symbol !== symbol));
      toast.success(`${symbol} removed from watchlist`);
    } catch { toast.error("Failed to remove"); }
  };


  // Count sectors
  const sectors = {};
  stocks.forEach(s => { sectors[s.sector] = (sectors[s.sector] || 0) + 1; });

  if (loading) {
    return (
      <div>
        <MarketTicker />
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-8 py-4 sm:py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-bt-surface border border-bt-border-subtle p-4">
              <Skeleton className="h-6 w-32 bg-bt-surface-2 mb-3" />
              <Skeleton className="h-4 w-full bg-bt-surface-2 mb-2" />
              <Skeleton className="h-4 w-2/3 bg-bt-surface-2" />
            </div>
          ))}
        </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <MarketTicker />
      <div className="max-w-[1600px] mx-auto px-3 sm:px-4 md:px-8 py-4 sm:py-6">
        {/* Title + Market Status */}
        <div className="mb-8 stagger-1">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-4">
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-semibold tracking-tight text-bt-text-primary">
                Market Intelligence
              </h1>
              <p className="text-base text-bt-text-secondary font-body mt-2">
                Real-time analysis, AI recommendations & portfolio tracking
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
            <MarketCountdown />
            {lastUpdated && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-bt-surface border border-bt-border-subtle shrink-0" data-testid="live-indicator">
                <Clock className="w-3.5 h-3.5 text-bt-text-tertiary" strokeWidth={1.5} />
                <span className="font-mono text-xs text-bt-text-secondary">
                  {lastUpdated.toLocaleTimeString()}
                </span>
              </div>
            )}
            </div>
          </div>
          {/* Market Status Bar */}
          {marketStatus && (
            <div className="flex items-center gap-3 flex-wrap" data-testid="market-status-bar">
              {/* US Market */}
              <div className={`flex items-center gap-2 px-4 py-2 border ${
                marketStatus.us_market === "open" ? "bg-bt-positive/10 border-bt-positive/30" :
                marketStatus.us_market === "pre-market" || marketStatus.us_market === "after-hours" ? "bg-bt-gold/10 border-bt-gold/30" :
                "bg-bt-surface border-bt-border-subtle"
              }`} data-testid="us-market-status">
                <span className="relative flex h-2 w-2">
                  {marketStatus.us_market === "open" && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-bt-positive opacity-50" />}
                  <span className={`relative inline-flex rounded-full h-2 w-2 ${
                    marketStatus.us_market === "open" ? "bg-bt-positive" :
                    marketStatus.us_market === "pre-market" || marketStatus.us_market === "after-hours" ? "bg-bt-gold" :
                    "bg-bt-text-tertiary"
                  }`} />
                </span>
                <span className="font-mono text-xs font-medium text-bt-text-primary">US</span>
                <span className={`font-mono text-xs ${
                  marketStatus.us_market === "open" ? "text-bt-positive" :
                  marketStatus.us_market === "pre-market" || marketStatus.us_market === "after-hours" ? "text-bt-gold" :
                  "text-bt-text-tertiary"
                }`}>
                  {marketStatus.us_market === "open" ? "OPEN" :
                   marketStatus.us_market === "pre-market" ? "PRE-MARKET" :
                   marketStatus.us_market === "after-hours" ? "AFTER-HOURS" : "CLOSED"}
                </span>
                <span className="text-xs text-bt-text-tertiary font-mono hidden sm:inline">{marketStatus.us_label}</span>
                <span className="text-xs text-bt-text-tertiary font-mono">{marketStatus.us_time}</span>
              </div>
              {/* Crypto */}
              <div className="flex items-center gap-2 px-4 py-2 bg-bt-positive/10 border border-bt-positive/30" data-testid="crypto-market-status">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-bt-positive opacity-50" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-bt-positive" />
                </span>
                <span className="font-mono text-xs font-medium text-bt-text-primary">Crypto</span>
                <span className="font-mono text-xs text-bt-positive">24/7</span>
              </div>
              {/* Finnhub */}
              <div className="flex items-center gap-2 px-4 py-2 bg-bt-surface border border-bt-border-subtle" data-testid="data-source-status">
                <Radio className="w-3.5 h-3.5 text-bt-gold" strokeWidth={1.5} />
                <span className="font-mono text-xs text-bt-text-secondary">
                  Finnhub: {marketStatus.finnhub_stocks}/{stocks.length} live
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {/* Portfolio Summary */}
          <PortfolioSummary portfolio={portfolio} woodrow={woodrow} isAdmin={user?.role === "superadmin"} />

          {/* Sectors */}
          <div className="lg:col-span-2 bg-bt-surface border border-bt-border-subtle p-6 stagger-3" data-testid="sectors-panel">
            <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary mb-4">Sectors Tracked</h2>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(sectors).slice(0, 6).map(([sector, count]) => (
                <SectorBadge key={sector} sector={sector} count={count} />
              ))}
            </div>
          </div>

          {/* Top Movers */}
          <div className="md:col-span-2 bg-bt-surface border border-bt-border-subtle stagger-4" data-testid="top-movers-panel">
            <Tabs defaultValue="gainers" className="w-full">
              <div className="flex items-center justify-between px-4 pt-4 pb-2">
                <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary">Top Movers</h2>
                <TabsList className="bg-bt-surface-2 h-7">
                  <TabsTrigger value="gainers" className="text-xs px-3 h-6 data-[state=active]:bg-bt-positive/20 data-[state=active]:text-bt-positive" data-testid="tab-gainers">
                    Gainers
                  </TabsTrigger>
                  <TabsTrigger value="losers" className="text-xs px-3 h-6 data-[state=active]:bg-bt-negative/20 data-[state=active]:text-bt-negative" data-testid="tab-losers">
                    Losers
                  </TabsTrigger>
                </TabsList>
              </div>
              <TabsContent value="gainers" className="mt-0">
                {overview?.top_gainers?.map(s => <StockRow key={s.symbol} stock={s} />) || <p className="p-4 text-sm text-bt-text-tertiary">Loading...</p>}
              </TabsContent>
              <TabsContent value="losers" className="mt-0">
                {overview?.top_losers?.map(s => <StockRow key={s.symbol} stock={s} />) || <p className="p-4 text-sm text-bt-text-tertiary">Loading...</p>}
              </TabsContent>
            </Tabs>
          </div>

          {/* Crypto Quick */}
          <div className="bg-bt-surface border border-bt-border-subtle stagger-5" data-testid="crypto-quick-panel">
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary">Crypto</h2>
              <Link to="/crypto" className="text-xs font-mono text-bt-gold hover:text-bt-gold-light" data-testid="view-crypto-link">
                View All <ArrowRight className="inline w-3 h-3" />
              </Link>
            </div>
            {crypto.map(c => {
              const change = c.price_change_percentage_24h || 0;
              return (
                <div key={c.id} className="flex items-center justify-between px-4 py-2.5 border-b border-bt-border-subtle last:border-0">
                  <div className="flex items-center gap-2">
                    {c.image && <img src={c.image} alt={c.name} className="w-5 h-5 rounded-full" />}
                    <span className="font-mono text-xs font-medium text-bt-text-primary uppercase">{c.symbol}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-xs text-bt-text-primary">${c.current_price?.toLocaleString()}</p>
                    <p className={`font-mono text-xs ${change >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>
                      {change >= 0 ? "+" : ""}{change?.toFixed(1)}%
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Watchlist */}
          <div className="bg-bt-surface border border-bt-border-subtle stagger-6" data-testid="watchlist-panel">
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-bt-gold" strokeWidth={1.5} />
                <h2 className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary">Watchlist</h2>
              </div>
              <Badge variant="outline" className="text-xs font-mono border-bt-border-subtle text-bt-text-tertiary">{watchlist.length}</Badge>
            </div>
            <ScrollArea className="max-h-[260px]">
              {watchlist.length === 0 ? (
                <p className="px-4 py-6 text-xs text-bt-text-tertiary text-center font-body">
                  No stocks in watchlist. Search and add stocks to track them here.
                </p>
              ) : (
                watchlist.map(w => (
                  <div key={w.symbol} className="flex items-center justify-between px-4 py-2.5 border-b border-bt-border-subtle last:border-0 group">
                    <Link to={`/stock/${w.symbol}`} className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-xs font-medium text-bt-text-primary">{w.symbol}</span>
                      {w.price && <span className="font-mono text-xs text-bt-text-secondary">${w.price.toLocaleString()}</span>}
                    </Link>
                    <div className="flex items-center gap-2">
                      {w.change_pct !== undefined && (
                        <span className={`font-mono text-xs ${w.change_pct >= 0 ? "text-bt-positive" : "text-bt-negative"}`}>
                          {w.change_pct >= 0 ? "+" : ""}{w.change_pct}%
                        </span>
                      )}
                      <button
                        onClick={() => handleRemoveWatchlist(w.symbol)}
                        className="opacity-0 group-hover:opacity-100 text-bt-text-tertiary hover:text-bt-negative transition-all"
                        data-testid={`watchlist-remove-${w.symbol}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </ScrollArea>
          </div>

          <AllStocksPanel stocks={stocks} />
        </div>
      </div>
    </div>
  );
}
